/******/ (() => { // webpackBootstrap
/**
 * Cart Items Web Component
 * Handles quantity updates and item removal on the cart template page
 * using Shopify's section rendering API for seamless updates without page reload.
 */
class CartItems extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.getAttribute('data-section-id') || 'template--cart';
    this.cartCountIndicator = document.querySelector('[data-cart-count-indicator]');
    
    // Bind methods
    this.handleQuantityButtonClick = this.handleQuantityButtonClick.bind(this);
    this.handleQuantityInputChange = this.handleQuantityInputChange.bind(this);
    this.handleRemoveItem = this.handleRemoveItem.bind(this);
    this.updateCart = this.updateCart.bind(this);
    this.updateLineItem = this.updateLineItem.bind(this);
    this.renderCartSection = this.renderCartSection.bind(this);
    this.handleCartErrors = this.handleCartErrors.bind(this);
  }

  connectedCallback() {
    window.wetheme.webcomponentRegistry.register({ key: 'component-cart-items' });
    
    // Use event delegation for dynamic content
    this.addEventListener('click', this.handleClick.bind(this));
    this.addEventListener('change', this.handleChange.bind(this));
  }

  handleClick(e) {
    if (this.hasAttribute('aria-busy')) {
      e.preventDefault();
      return;
    }
    /* ===== Handle delegated click events ===== */
    const quantityButton = e.target.closest('[data-cart-quantity-button]');
    const removeButton = e.target.closest('[data-cart-remove-item]');

    if (quantityButton) {
      e.preventDefault();
      this.handleQuantityButtonClick(quantityButton);
    } else if (removeButton) {
      e.preventDefault();
      this.handleRemoveItem(removeButton);
    }
  }

  handleChange(e) {
    /* ===== Handle delegated change events ===== */
    const quantityInput = e.target.closest('[data-cart-quantity-input]');
    if (quantityInput) {
      e.preventDefault();
      this.handleQuantityInputChange(quantityInput);
    }
  }

  handleQuantityButtonClick(button) {
    /* ===== Handle quantity button (+/-) click ===== */
    const lineItem = button.closest('[data-cart-item]');
    if (!lineItem) return;

    const input = lineItem.querySelector('[data-cart-quantity-input]');
    if (!input) return;

    const previousValue = parseInt(input.value);
    const adjustment = button.getAttribute('name') === 'plus' ? 1 : -1;
    let newValue = previousValue + adjustment;

    // Ensure minimum of 1
    if (newValue < 1) newValue = 1;

    // Only update if value changed
    if (newValue !== previousValue) {
      input.value = newValue;
      const lineIndex = parseInt(lineItem.getAttribute('data-item-index'));
      this.updateCart(lineItem, lineIndex, newValue);
    }
  }

  handleQuantityInputChange(input) {
    /* ===== Handle direct quantity input change ===== */
    const lineItem = input.closest('[data-cart-item]');
    if (!lineItem) return;

    let newValue = parseInt(input.value);
    
    // Validate input
    if (isNaN(newValue) || newValue < 1) {
      newValue = 1;
      input.value = newValue;
    }

    const lineIndex = parseInt(lineItem.getAttribute('data-item-index'));
    this.updateCart(lineItem, lineIndex, newValue);
  }

  handleRemoveItem(button) {
    /* ===== Handle remove item button click ===== */
    const lineItem = button.closest('[data-cart-item]');
    if (!lineItem) return;

    const lineIndex = parseInt(lineItem.getAttribute('data-item-index'));
    this.updateCart(lineItem, lineIndex, 0);
  }

  async updateCart(lineItem, line, quantity) {
    if (this.hasAttribute('aria-busy')) return;
    this.setAttribute('aria-busy', 'true');
    this.querySelectorAll('[name="checkout"]').forEach(button => { button.disabled = true; });
    try {
      /* ===== Set loading state ===== */
      this.setLoadingState(lineItem);

      /* ===== If removing, set remove loading state ===== */
      if (quantity === 0) {
        this.setRemoveLoadingState(lineItem);
      }

      /* ===== Update the line item quantity ===== */
      const response = await this.updateLineItem(line, quantity);
      if (!response) {
        this.handleCartErrors(line);
        return;
      }
      if (response.item_count === 0) {
        window.location.assign(window.routes.cart_url);
        return;
      }

      /* ===== Update only the changed cart content, preserving quantity focus ===== */
      if (!this.renderCartSection(response)) {
        window.location.assign(window.routes.cart_url);
        return;
      }

      /* ===== Update the cart drawer if it exists ===== */
      window.eventBus.emit('update:cart:drawer', response);

      /* ===== Update cart count indicator ===== */
      this.updateCartCountIndicator();

    } catch (error) {
      console.error('Error updating cart:', error);
      this.hideLoadingState(lineItem);
    } finally {
      this.hideLoadingState(lineItem);
      this.hideRemoveLoadingState(lineItem);
      this.removeAttribute('aria-busy');
      this.querySelectorAll('[name="checkout"]').forEach(button => { button.disabled = false; });
    }
  }

  async updateLineItem(line, quantity) {
    try {
      /* ===== Update line item quantity via cart change API ===== */
      const body = JSON.stringify({
        line,
        quantity,
        sections: this.sectionId
      });

      const response = await fetch(window.routes.cart_change_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body
      });

      const responseText = await response.text();

      if (!responseText) {
        this.handleCartErrors(line);
        return null;
      }

      const parsedResponse = JSON.parse(responseText);

      /* ===== Handle errors ===== */
      if (parsedResponse.errors) {
        this.handleCartErrors(line, parsedResponse.errors);
        return null;
      }

      return parsedResponse;
    } catch (error) {
      console.error('Error updating cart line item:', error);
      return null;
    }
  }

  renderCartSection(response) {
    /* ===== Reconcile the rendered section without rebuilding stable cart rows ===== */
    const sectionHtml = response.sections?.[this.sectionId];
    if (!sectionHtml) return false;

    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const gridSelector = '[data-cart-dynamic-content="cart-items"]';
    const footerSelector = '[data-cart-dynamic-content="cart-footer"]';
    const currentGrid = this.querySelector(gridSelector);
    const updatedGrid = doc.querySelector(gridSelector);
    const currentFooter = this.querySelector(footerSelector);
    const updatedFooter = doc.querySelector(footerSelector);
    if (!currentGrid || !updatedGrid || !currentFooter || !updatedFooter) return false;

    const currentRows = Array.from(currentGrid.querySelectorAll('[data-cart-item]'));
    const updatedRows = Array.from(updatedGrid.querySelectorAll('[data-cart-item]'));
    const sameRows = currentRows.length === updatedRows.length && currentRows.every((row, index) => {
      const updatedRow = updatedRows[index];
      return row.dataset.cartItemKey && row.dataset.cartItemKey === updatedRow.dataset.cartItemKey &&
        ['.cart-grid__price', '.cart-grid__total', '[data-cart-quantity-input]'].every(selector =>
          row.querySelector(selector) && updatedRow.querySelector(selector)
        );
    });

    if (!sameRows) {
      // Removal, promotion, or cart-line changes may require new rows and controls.
      currentGrid.innerHTML = updatedGrid.innerHTML;
      currentFooter.innerHTML = updatedFooter.innerHTML;
      return true;
    }

    currentRows.forEach((row, index) => {
      const updatedRow = updatedRows[index];
      ['.cart-grid__price', '.cart-grid__total'].forEach(selector => {
        const current = row.querySelector(selector);
        const updated = updatedRow.querySelector(selector);
        if (current.innerHTML !== updated.innerHTML) current.innerHTML = updated.innerHTML;
      });

      const input = row.querySelector('[data-cart-quantity-input]');
      const updatedInput = updatedRow.querySelector('[data-cart-quantity-input]');
      input.value = updatedInput.value;
      input.setAttribute('value', updatedInput.value);
      const error = row.querySelector('[data-cart-error-index]');
      if (error) {
        error.textContent = '';
        error.setAttribute('aria-hidden', 'true');
      }
    });

    this.updateCartFooter(currentFooter, updatedFooter);
    return true;
  }

  updateCartFooter(currentFooter, updatedFooter) {
    const currentSubtotal = currentFooter.querySelector('.cart__subtotal-container');
    const updatedSubtotal = updatedFooter.querySelector('.cart__subtotal-container');
    const currentDiscounts = currentFooter.querySelector('.cart--order-discount-wrapper')?.outerHTML;
    const updatedDiscounts = updatedFooter.querySelector('.cart--order-discount-wrapper')?.outerHTML;
    const currentQuote = currentFooter.querySelector('.shipping-quote-cart-notice')?.outerHTML;
    const updatedQuote = updatedFooter.querySelector('.shipping-quote-cart-notice')?.outerHTML;

    if (!currentSubtotal || !updatedSubtotal || currentDiscounts !== updatedDiscounts || currentQuote !== updatedQuote) {
      currentFooter.innerHTML = updatedFooter.innerHTML;
      return;
    }

    if (currentSubtotal.innerHTML !== updatedSubtotal.innerHTML) {
      currentSubtotal.innerHTML = updatedSubtotal.innerHTML;
    }
  }

  handleCartErrors(line, errors) {
    /* ===== Handle cart update errors ===== */
    const lineItem = this.querySelector(`[data-item-index="${line}"]`);
    if (lineItem) {
      this.hideLoadingState(lineItem);
      this.hideRemoveLoadingState(lineItem);
      
      // Reset input to previous value
      const input = lineItem.querySelector('[data-cart-quantity-input]');
      if (input) {
        input.value = input.getAttribute('value');
      }
    }

    /* ===== Display error message ===== */
    if (errors) {
      this.renderErrorMessage(line, errors);
    }
  }

  renderErrorMessage(line, message) {
    /* ===== Display error message for a line item ===== */
    const errorElement = this.querySelector(`[data-cart-error-index="${line}"]`);
    if (!errorElement) return;
    
    errorElement.innerHTML = message;
    errorElement.setAttribute('aria-hidden', 'false');
  }

  async getCart() {
    /* ===== Fetch cart data ===== */
    try {
      const response = await fetch(`${window.routes.cart_url}?view=compare`);
      return await response.json();
    } catch (error) {
      console.error(`Error fetching cart: ${error.message}`);
      return null;
    }
  }

  updateCartCountIndicator() {
    /* ===== Update the cart count indicator in the header ===== */
    this.getCart().then(cart => {
      if (cart) {
        window.wetheme.updateCartCount(cart);
      }
    });
  }

  setLoadingState(lineItem) {
    lineItem?.classList.add('is-loading');
  }

  hideLoadingState(lineItem) {
    lineItem?.classList.remove('is-loading');
  }

  setRemoveLoadingState(lineItem) {
    lineItem?.classList.add('is-removing');
  }

  hideRemoveLoadingState(lineItem) {
    lineItem?.classList.remove('is-removing');
  }
}

customElements.define('cart-items', CartItems);

/******/ })()
;
